import { getErrorMessage } from '../../../api/unwrapQuery';
import React, { useEffect, useState } from 'react';
import { Button, Input, FormField } from '@neshane/blueberry';
import { startAuthentication } from '@simplewebauthn/browser';
import { STEP_UP_ACTIONS } from '../hooks/useStepUp';
import { verifyFactor, authenticateWithPasskey, getPasskeyAuthenticationOptions, verifyRecoveryCode } from '../api/securityApi';
import './StepUpDialog.css';

const METHODS = [
    { id: 'totp', label: 'کد یک بار مصرف' },
    { id: 'passkey', label: 'Passkey' },
    { id: 'recovery', label: 'کد بازیابی' },
];

const StepUpDialog = ({ action, onSuccess, onClose }) => {
    const [method, setMethod] = useState('totp');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [passkeyError, setPasskeyError] = useState(null);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const actionLabel = STEP_UP_ACTIONS[action] || 'این عملیات';

    const handleOtpOrRecoverySubmit = async (e) => {
        e.preventDefault();
        const trimmed = code.trim();
        if (!trimmed) return;
        setLoading(true);
        setError(null);
        try {
            let response;
            if (method === 'totp') {
                response = await verifyFactor('totp', trimmed);
            } else {
                response = await verifyRecoveryCode(trimmed);
            }
            const token = response?.stepUpToken;
            if (!token) throw new Error('پاسخی از سرور دریافت نشد');
            onSuccess(token);
        } catch (err) {
            setError(getErrorMessage(err, 'خطا در احراز هویت') || 'خطا در تأیید هویت');
        } finally {
            setLoading(false);
        }
    };

    const handlePasskeyAuth = async () => {
        setLoading(true);
        setPasskeyError(null);
        setError(null);
        try {
            const options = await getPasskeyAuthenticationOptions();
            const assertion = await startAuthentication(options);
            const response = await authenticateWithPasskey(assertion);
            const token = response?.stepUpToken;
            if (!token) throw new Error('پاسخی از سرور دریافت نشد');
            onSuccess(token);
        } catch (err) {
            if (err?.name === 'NotAllowedError') {
                setPasskeyError('درخواست تأیید لغو شد یا Passkey در دسترس نبود');
            } else {
                setPasskeyError(getErrorMessage(err, 'خطا در احراز هویت') || 'خطا در تأیید با Passkey');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="step-up-overlay" onClick={onClose}>
            <div className="step-up-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="step-up-dialog__header">
                    <span className="step-up-dialog__shield">🛡️</span>
                    <h2 className="step-up-dialog__title">تأیید مجدد هویت</h2>
                    <p className="step-up-dialog__subtitle">
                        برای انجام «{actionLabel}» لازم است دوباره هویت خود را تأیید کنید.
                    </p>
                </div>

                <div className="step-up-dialog__methods">
                    {METHODS.map((m) => (
                        <button
                            key={m.id}
                            type="button"
                            className={`step-up-dialog__method ${method === m.id ? 'step-up-dialog__method--active' : ''}`}
                            onClick={() => {
                                setMethod(m.id);
                                setError(null);
                                setPasskeyError(null);
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>

                <div className="step-up-dialog__body">
                    {method === 'passkey' ? (
                        <>
                            {passkeyError && <div className="step-up-dialog__error">{passkeyError}</div>}
                            <Button
                                color="primary"
                                fit="parent"
                                isDisabled={loading}
                                onClick={handlePasskeyAuth}
                                text={loading ? 'در حال بررسی…' : 'استفاده از Passkey این دستگاه'}
                            />
                        </>
                    ) : (
                        <>
                            <FormField
                                label={method === 'totp' ? 'کد یک بار مصرف اپلیکیشن احراز هویت' : 'کد بازیابی ۱۲ رقمی'}
                                htmlFor="step-up-code" infoTipContent="Enter step-up-code">
                                <Input
                                    id="step-up-code"
                                    fit="parent"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder={method === 'totp' ? '••••••' : 'XXXX-XXXX-XXXX'}
                                    maxLength={method === 'totp' ? 6 : 18}
                                    autoFocus
                                />
                            </FormField>
                            {error && <div className="step-up-dialog__error">{error}</div>}
                            <Button
                                color="primary"
                                fit="parent"
                                isDisabled={loading || code.trim() === ''}
                                onClick={handleOtpOrRecoverySubmit}
                                text={loading ? 'در حال بررسی…' : 'تأیید'}
                            />
                        </>
                    )}
                </div>

                <div className="step-up-dialog__footer">
                    <Button variant="outline" onClick={onClose} isDisabled={loading} text="انصراف" />
                </div>

                <button type="button" className="step-up-dialog__close" onClick={onClose} aria-label="بستن">
                    ✕
                </button>
            </div>
        </div>
    );
};

export default StepUpDialog;